import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { fadeUp, stagger } from '@/lib/motion';
import { Section, SectionTitle, AuthorityBadge } from '@/components/shared/Section';
import { ArrowRight, MessageCircle, Zap, Bot, FileText, Cog, Eye, Rocket, Clock, CheckCircle, Shield, Puzzle, Users, Target, Database, TrendingUp, Brain, Activity } from 'lucide-react';

// ===== DATA =====

const coreCaps = [
  { icon: Bot, title: 'Agentes IA', desc: 'Diseñamos y configuramos agentes para atención, soporte, operación y asistencia interna.' },
  { icon: FileText, title: 'Prompts profesionales', desc: 'Estructuramos instrucciones efectivas para mejorar precisión, consistencia y utilidad.' },
  { icon: Cog, title: 'Automatización', desc: 'Conectamos IA con tareas, flujos, validaciones y procesos reales del negocio.' },
  { icon: Rocket, title: 'Prototipado rápido', desc: 'Levantamos pruebas conceptuales, mockups funcionales e implementaciones en tiempos concretos.' },
];

const fullCaps = [
  { icon: Bot, title: 'Configuración de agentes IA', desc: 'Agentes orientados a tareas reales: atención, soporte, operación, reportería, venta y consulta de información.' },
  { icon: FileText, title: 'Diseño de prompts profesionales', desc: 'Prompts efectivos, precisos y orientados a negocio para mejorar consistencia, calidad y toma de decisiones.' },
  { icon: Cog, title: 'Automatización de procesos', desc: 'IA conectada con tareas repetitivas, validaciones, formularios, flujos internos y procesos operacionales.' },
  { icon: Zap, title: 'Pruebas conceptuales rápidas', desc: 'Prueba conceptual en 48 horas para validar enfoque, experiencia y potencial del proyecto.' },
  { icon: Eye, title: 'Mockups y visualizadores', desc: 'Primera experiencia visual o funcional del proyecto en 4 días.' },
  { icon: Rocket, title: 'Implementación acelerada', desc: 'Ejecución en 30 días dependiendo del alcance, integraciones y complejidad operativa.' },
  { icon: Database, title: 'Data Lakes con OpenClaw', desc: 'Administramos data lakes empresariales, centralizando datos de múltiples fuentes para análisis avanzado.' },
  { icon: TrendingUp, title: 'Análisis predictivo de Big Data', desc: 'Modelos de IA que predicen comportamientos, tendencias de mercado y oportunidades de negocio.' },
  { icon: Brain, title: 'Simulación de tendencias', desc: 'Simulamos escenarios de mercado, productos y comportamientos para anticipar movimientos estratégicos.' },
];

const methodology = [
  { step: '01', title: 'Entendemos el desafío', desc: 'Analizamos el problema, la necesidad o la oportunidad.' },
  { step: '02', title: 'Diseñamos la solución', desc: 'Definimos agente, lógica, interacción, automatización, experiencia y alcance.' },
  { step: '03', title: 'Prueba rápida', desc: 'Construimos una prueba conceptual que permita visualizar el potencial.' },
  { step: '04', title: 'Mockup funcional', desc: 'Experiencia demostrable para validar diseño, flujo y usabilidad.' },
  { step: '05', title: 'Implementamos', desc: 'Solución operativa con foco en negocio y escalabilidad.' },
];

const mosaicImages = [
  { url: '/allcom_ia_dashboards.jpg', caption: 'Dashboards' },
  { url: '/allcom_ia_analytics.jpg', caption: 'Analytics' },
  { url: '/allcom_ia_app_celular.jpg', caption: 'App Celular' },
  { url: '/allcom_ia_produccion.jpg', caption: 'Producción' },
];

const useCases = [
  'Agentes de atención al cliente', 'Asistentes internos para colaboradores',
  'Automatización de respuestas y derivaciones', 'IA para consulta documental o técnica',
  'Kioscos inteligentes con interacción conversacional', 'Experiencias guiadas de autoservicio',
  'Prototipos rápidos de productos digitales', 'Flujos automatizados de validación y seguimiento',
  'Herramientas de apoyo para ventas y soporte', 'Dashboards con lectura inteligente de datos',
  'Predicción de eventos en equipos IoT', 'Análisis de comportamiento humano y patrones',
  'Simulación de tendencias de mercado', 'Modelos predictivos de demanda y comportamiento',
];

const plans = [
  { name: 'Consultoría IA Inicial', price: '4', desc: 'Diagnóstico rápido, análisis de oportunidad y recomendación de camino con informe detallado.',
    includes: ['Reunión de levantamiento y análisis', 'Identificación de oportunidades con IA', 'Recomendación de enfoque y prioridades', 'Informe detallado con KPIs posibles', 'Imágenes conceptuales de la solución'],
    cta: 'Solicitar este plan' },
  { name: 'Plan Mockup IA', price: '7', desc: 'Diseño de solución + mockup o visualizador funcional en 4 días con KPIs e imágenes conceptuales.',
    includes: ['Sesión de levantamiento del caso de uso', 'Diseño del flujo general e interacción', 'Mockup o visualizador funcional', 'Informe con KPIs y sugerencias', 'Presentación ejecutiva del proyecto'],
    cta: 'Solicitar este plan', featured: true },
  { name: 'Consultoría Completa', price: '12', desc: 'Trabajo intensivo de una semana: diseño funcional, prompts, automatizaciones y roadmap de implementación.',
    includes: ['Diagnóstico de negocio y operación', 'Diseño funcional del agente o solución', 'Estructura de prompts profesionales', 'Roadmap de implementación completo', 'Propuesta formal + presentación ejecutiva'],
    cta: 'Solicitar este plan' },
];

// ===== CTA BLOCK =====
const CTABlock = ({ variant = 'default' }) => (
  <div className={`flex flex-col sm:flex-row items-center gap-3 ${variant === 'center' ? 'justify-center' : ''}`}>
    <Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-[0_10px_24px_rgba(14,165,233,0.35)] transition-all">
      Agendar consultoría <ArrowRight className="w-4 h-4" />
    </Link>
    <a href="https://wa.me/56975294340?text=Hola,%20me%20interesa%20ALLCOM%20IA" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-emerald-600 border border-emerald-200 rounded-xl hover:bg-emerald-50 transition-colors">
      <MessageCircle className="w-4 h-4" /> WhatsApp
    </a>
  </div>
);

const MicroCopy = () => (
  <p className="text-[10px] text-[#94A3B8] mt-2 text-center">Respuesta inicial en menos de 24 horas</p>
);

// ===== PAGE =====
export default function IAPage() {
  const [kpiRef, kpiInView] = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <div>
      {/* ===== HERO ===== */}
      <div className="relative overflow-hidden bg-[#050E1F] text-white">
        <div className="absolute inset-0 noise-texture" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <AuthorityBadge icon={Zap}>ALLCOM IA STUDIO</AuthorityBadge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold tracking-tight leading-tight mb-5" style={{fontFamily:'Syne,sans-serif'}}>
              Inteligencia artificial aplicada al negocio, con foco real en resultados.
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-3">
              En ALLCOM llevamos 5 años trabajando en soluciones de inteligencia artificial aplicadas a operación, atención, automatización y experiencia de cliente.
            </p>
            <p className="text-sm text-slate-400 leading-relaxed mb-8">
              Configuramos agentes, diseñamos prompts, automatizamos procesos y convertimos ideas en soluciones funcionales.
            </p>
            <CTABlock />
            <p className="text-[10px] text-slate-500 mt-3">Respuesta inicial en menos de 24 horas</p>
          </div>
        </div>
      </div>

      {/* ===== SPEED KPIs ===== */}
      <div className="bg-white border-b border-slate-100">
        <div ref={kpiRef} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-center text-xs font-semibold text-[#64748B] uppercase tracking-[0.18em] mb-8">Lo que podemos lograr</p>
          <div className="grid grid-cols-3 gap-6 text-center">
            {[{ value: 48, suffix: 'h', label: 'Prueba conceptual' }, { value: 4, suffix: ' días', label: 'Mockup funcional' }, { value: 30, suffix: ' días', label: 'Implementación' }].map((k, i) => (
              <div key={i}>
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>
                  {kpiInView ? <CountUp end={k.value} duration={1.5} /> : '0'}<span className="text-lg sm:text-xl text-[#0EA5E9]">{k.suffix}</span>
                </div>
                <div className="text-sm text-[#64748B] mt-1 font-medium">{k.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ===== QUÉ HACEMOS CON IA, EN CONCRETO ===== */}
      <Section>
        <SectionTitle badge="EN CONCRETO" title="Qué hacemos con IA, en concreto" center />
        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
          {coreCaps.map((cap, i) => (
            <motion.div key={i} variants={fadeUp} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm text-center">
              <cap.icon className="w-7 h-7 text-[#0A2540] mx-auto mb-4" strokeWidth={1.6} />
              <h3 className="text-sm font-semibold text-[#0A2540] mb-2" style={{fontFamily:'Syne,sans-serif'}}>{cap.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed">{cap.desc}</p>
            </motion.div>
          ))}
        </motion.div>
        <div className="mt-8 text-center">
          <CTABlock variant="center" />
          <MicroCopy />
        </div>
      </Section>

      {/* ===== SERVICIOS COMPLETOS ===== */}
      <Section alt>
        <SectionTitle badge="QUÉ HACEMOS" title="¿Qué hacemos en ALLCOM IA Studio?" subtitle="5 años de experiencia real configurando soluciones — no solo herramientas — que conectan IA con la operación real del cliente." center />
        <motion.div variants={stagger} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {fullCaps.map((cap, i) => (
            <motion.div key={i} variants={fadeUp} className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm text-center">
              <cap.icon className="w-7 h-7 text-[#0A2540] mx-auto mb-4" strokeWidth={1.6} />
              <h3 className="text-sm font-semibold text-[#0A2540] mb-2" style={{fontFamily:'Syne,sans-serif'}}>{cap.title}</h3>
              <p className="text-xs text-[#64748B] leading-relaxed">{cap.desc}</p>
            </motion.div>
          ))}
        </motion.div>
        {/* Partnerships */}
        <div className="mt-12 max-w-3xl mx-auto text-center">
          <p className="text-xs text-[#64748B] leading-relaxed mb-3">Contamos con contratos directos con proveedores globales de LLM, MCP y plataformas de IA enterprise. El valor no está en la herramienta, sino en cómo diseñamos e implementamos la solución para cada cliente.</p>
          <div className="flex justify-center items-center gap-6 flex-wrap mt-4">
            <div className="px-4 py-2 rounded-lg bg-slate-100 border border-slate-200">
              <span className="text-xs font-semibold text-[#0A2540]">LLM Enterprise</span>
            </div>
            <div className="px-4 py-2 rounded-lg bg-slate-100 border border-slate-200">
              <span className="text-xs font-semibold text-[#0A2540]">MCP Protocols</span>
            </div>
            <div className="px-4 py-2 rounded-lg bg-slate-100 border border-slate-200">
              <span className="text-xs font-semibold text-[#0A2540]">OpenClaw</span>
            </div>
            <div className="px-4 py-2 rounded-lg bg-slate-100 border border-slate-200">
              <span className="text-xs font-semibold text-[#0A2540]">Data Lakes</span>
            </div>
          </div>
        </div>
      </Section>

      {/* ===== METODOLOGÍA ===== */}
      <Section>
        <SectionTitle badge="METODOLOGÍA" title="Nuestra propuesta" subtitle="La IA no sirve si queda en una presentación. Sirve cuando se aterriza, se prueba y se convierte en una herramienta concreta." center />
        <div className="max-w-3xl mx-auto mb-10">
          <div className="space-y-4">
            {methodology.map((m, i) => (
              <motion.div key={i} variants={fadeUp} className="flex items-start gap-5 p-5 rounded-xl bg-white border border-slate-200 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-[#0A2540] text-white text-xs font-bold flex items-center justify-center shrink-0" style={{fontFamily:'JetBrains Mono,monospace'}}>{m.step}</div>
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>{m.title}</h4>
                  <p className="text-xs text-[#64748B] mt-1 leading-relaxed">{m.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        {/* Mosaic */}
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-[10px] text-slate-400 uppercase tracking-[0.18em] mb-4">Visualizadores, dashboards, asistentes y experiencias funcionales diseñadas para operación, atención y toma de decisiones.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {mosaicImages.map((img, i) => (
              <div key={i} className="relative rounded-xl overflow-hidden aspect-[4/3] select-none group">
                <img src={img.url} alt={img.caption} className="w-full h-full object-cover" loading="lazy" draggable="false" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="text-[10px] text-white font-medium text-center">{img.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== POR QUÉ ALLCOM IA + BUILDING ===== */}
      <Section alt>
        <SectionTitle badge="POR QUÉ ALLCOM IA STUDIO" title="¿Por qué trabajar con nosotros?" center />
        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-xl">
            <img src="/allcom_oficina_providencia_santiago_chile.jpg" alt="ALLCOM IA Studio" className="w-full h-full object-cover object-bottom min-h-[300px]" loading="lazy" style={{objectPosition:'center 70%'}} />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050E1F]/85 via-[#050E1F]/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h3 className="text-lg font-semibold text-white mb-2" style={{fontFamily:'Syne,sans-serif'}}>ALLCOM IA Studio</h3>
              <div className="grid grid-cols-2 gap-2">
                {['Showroom funcional', 'Experience Center IA', 'Sala de reuniones', 'Área experiencia cliente'].map((item, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-[10px] text-slate-300"><CheckCircle className="w-3 h-3 text-[#0EA5E9] shrink-0" strokeWidth={2} />{item}</div>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-2">Andrés Bello 1567 · Providencia · Santiago · Chile</p>
            </div>
          </div>
          <div className="space-y-4">
            {[
              { icon: Shield, title: 'Experiencia real', desc: '5 años trabajando con IA desde la ejecución. Configuramos soluciones, no solo herramientas.' },
              { icon: Zap, title: 'Velocidad', desc: 'Concepto en 48h · Mockup en 4 días · Implementación en 30 días.' },
              { icon: Target, title: 'Enfoque práctico', desc: 'No hablamos de IA como teoría. La diseñamos para resolver necesidades concretas.' },
              { icon: Puzzle, title: 'Integración total', desc: 'Conectamos con pantallas, kioscos, software, flujos operativos y automatizaciones de negocio.' },
              { icon: Users, title: 'Acompañamiento consultivo', desc: 'Ayudamos al cliente a entender qué hacer, cómo priorizar y cómo implementar.' },
            ].map((d, i) => (
              <div key={i} className="flex items-start gap-4 p-4 rounded-xl bg-white border border-slate-200 shadow-sm">
                <d.icon className="w-5 h-5 text-[#0A2540] shrink-0 mt-0.5" strokeWidth={1.8} />
                <div>
                  <h4 className="text-sm font-semibold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>{d.title}</h4>
                  <p className="text-xs text-[#64748B] mt-0.5 leading-relaxed">{d.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ===== CASOS DE USO ===== */}
      <Section>
        <SectionTitle badge="CASOS DE USO" title="Dónde se puede aplicar" center />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl mx-auto">
          {useCases.map((uc, i) => (
            <div key={i} className="flex items-center gap-2.5 p-3 rounded-lg bg-white border border-slate-100">
              <CheckCircle className="w-4 h-4 text-[#0EA5E9] shrink-0" strokeWidth={2} />
              <span className="text-xs text-[#1E293B]">{uc}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ===== PLANES DE TRABAJO ===== */}
      <Section alt>
        <SectionTitle badge="PLANES" title="Planes de trabajo" center />
        <div className="grid lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div key={i} variants={fadeUp} className={`p-6 rounded-2xl border shadow-sm flex flex-col ${plan.featured ? 'border-[#0EA5E9] ring-1 ring-[#0EA5E9]/20 bg-white' : 'border-slate-200 bg-white'}`}>
              {plan.featured && <span className="text-[9px] px-2 py-0.5 bg-[#0EA5E9]/10 text-[#0EA5E9] rounded-full font-semibold self-start mb-3 uppercase tracking-wider">Más solicitado</span>}
              <h3 className="text-base font-semibold text-[#0A2540] mb-1" style={{fontFamily:'Syne,sans-serif'}}>{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-3xl font-bold text-[#0A2540]" style={{fontFamily:'Syne,sans-serif'}}>{plan.price}</span>
                <span className="text-sm text-[#64748B]">UF</span>
              </div>
              <p className="text-[10px] text-[#94A3B8] mb-3">Un solo pago + IVA</p>
              <p className="text-xs text-[#64748B] leading-relaxed mb-4">{plan.desc}</p>
              <ul className="space-y-2 mb-5 flex-1">
                {plan.includes.map((item, j) => (
                  <li key={j} className="flex items-start gap-2 text-xs text-[#1E293B]">
                    <CheckCircle className="w-3.5 h-3.5 text-[#0EA5E9] shrink-0 mt-0.5" strokeWidth={2} />{item}
                  </li>
                ))}
              </ul>
              <Link to="/contacto" className={`w-full text-center py-2.5 text-sm font-semibold rounded-xl transition-colors ${plan.featured ? 'bg-[#0EA5E9] text-white hover:bg-sky-600' : 'bg-slate-100 text-[#0A2540] hover:bg-slate-200'}`}>{plan.cta}</Link>
            </motion.div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <CTABlock variant="center" />
          <MicroCopy />
        </div>
      </Section>

      {/* ===== CEO QUOTE ===== */}
      <Section>
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-20 h-20 mx-auto mb-5 rounded-full overflow-hidden shadow-lg border-2 border-slate-200">
            <img src="/ceo-mario.jpg" alt="Mario Pino Corona" className="w-full h-full object-cover object-top" style={{filter:'grayscale(100%)'}} />
          </div>
          <blockquote className="text-lg sm:text-xl text-[#0A2540] font-medium leading-relaxed mb-4" style={{fontFamily:'Syne,sans-serif'}}>
            "En ALLCOM nos apasiona resolver problemas reales. Tomamos desafíos complejos y los convertimos en soluciones útiles, simples y rápidas de implementar."
          </blockquote>
          <div className="text-sm font-semibold text-[#0A2540]">Mario Pino Corona</div>
          <div className="text-xs text-[#64748B]">CEO & Fundador · ALLCOM</div>
        </div>
      </Section>

      {/* ===== CTA FINAL ===== */}
      <Section alt>
        <div className="max-w-3xl mx-auto">
          <div className="p-8 sm:p-10 rounded-2xl bg-[#0A2540] text-white text-center">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-3" style={{fontFamily:'Syne,sans-serif'}}>¿Tienes una idea y quieres verla funcionando rápido?</h2>
            <p className="text-sm text-slate-400 mb-2">En ALLCOM IA Studio te ayudamos a convertir una necesidad en una solución concreta.</p>
            <div className="flex justify-center gap-6 text-sm text-slate-300 mb-8">
              <span>Concepto en <strong className="text-white">48h</strong></span><span>·</span>
              <span>Mockup en <strong className="text-white">4 días</strong></span><span>·</span>
              <span>Implementación en <strong className="text-white">30 días</strong></span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contacto" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-white bg-[#0EA5E9] rounded-xl hover:bg-sky-600 shadow-lg">Agendar consultoría <ArrowRight className="w-4 h-4" /></Link>
              <a href="https://wa.me/56975294340?text=Hola,%20me%20interesa%20ALLCOM%20IA" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold text-emerald-400 border border-emerald-400/30 rounded-xl hover:bg-emerald-400/10"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
            </div>
            <p className="text-[10px] text-slate-500 mt-3">Respuesta inicial en menos de 24 horas</p>
          </div>
        </div>
      </Section>
    </div>
  );
}
